import { FlexColumn, FullContainer } from '@gorila-shared-ui/components';
import { SnackbarProvider } from 'baseui/snackbar';
import { ToasterContainer } from 'baseui/toast';
import React from 'react';
import { MapProvider } from 'react-map-gl/mapbox';
import { useRecoilValue } from 'recoil';
import { EXPANDED_SIDENAV_WIDTH, SIDENAV_WIDTH } from '../../constants/app';
import { useStyles } from '../../hooks/useStyles';
import { isLoggedInState, sidenavIsPinnedState } from '../../storage/app';
import PageLayout from '../layout/PageLayout';
import { AppProvider } from '../providers/AppProvider';
import { AuthProvider } from '../providers/AuthProvider';
import NotificationTypesProvider from '../providers/NotificationTypesProvider';
import { WorkspaceProvider } from '../providers/WorkspaceProvider';
import { WorkspacePicker } from '../workspace/WorkspacePicker';
import { HeadTitle } from './HeadTitle';

type Props = Readonly<{
  navbar: React.ReactNode;
  sidenav: React.ReactNode;
  withMarginLeft: boolean;
  providers?: React.ReactNode;
}>;

function DashboardWrapper({ navbar, sidenav, withMarginLeft, providers }: Props) {
  const { css, theme } = useStyles();
  const isLoggedIn = useRecoilValue(isLoggedInState);
  const sidenavIsPinned = useRecoilValue(sidenavIsPinnedState);

  const marginLeft = (() => {
    if (!withMarginLeft) return;
    if (sidenavIsPinned) {
      return `${EXPANDED_SIDENAV_WIDTH}px`;
    }
    return `${SIDENAV_WIDTH}px`;
  })();

  return (
    <MapProvider>
      <SnackbarProvider placement="top">
        <AuthProvider />
        <HeadTitle />
        {isLoggedIn && (
          <>
            <AppProvider />
            <WorkspaceProvider />
            <NotificationTypesProvider />
            {providers}
            <FullContainer flexible>
              {sidenav}
              <FlexColumn
                classNames={css({
                  flex: 1,
                  overflowX: 'hidden',
                  marginLeft: marginLeft,
                  transition: 'margin-left 0.3s ease-in-out',
                })}
                gap={0}
              >
                {navbar}
                <PageLayout />
              </FlexColumn>
            </FullContainer>
            <WorkspacePicker />
            <ToasterContainer
              autoHideDuration={2500}
              placement="topRight"
              resetAutoHideTimerOnUpdate
              usePortal={false}
              closeable={true}
              overrides={{
                Root: {
                  style: {
                    marginTop: '64px',
                    marginRight: theme.sizing.scale400,
                  },
                },
              }}
            />
          </>
        )}
      </SnackbarProvider>
    </MapProvider>
  );
}

export default DashboardWrapper;
